import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import styles from "./BookingPage.module.css";

// Country routing configuration - easy to update
const COUNTRY_ROUTING = {
  // HubSpot links
  links: {
    sara: "https://meetings.hubspot.com/sara-temboury-becerril",
    glenn: "https://meetings.hubspot.com/glen-mcclintock/besty-ai-demo",
    edward: "https://meetings.hubspot.com/edward206/besty-ai-demo",
    zoom: "https://us06web.zoom.us/webinar/register/WN_lejjIQsUT1GIrFSt6WK0nQ#/registration",
  },
  // European countries
  europe: [
    "Albania",
    "Andorra",
    "Austria",
    "Belarus",
    "Belgium",
    "Bosnia and Herzegovina",
    "Bulgaria",
    "Croatia",
    "Cyprus",
    "Czech Republic",
    "Denmark",
    "Estonia",
    "Finland",
    "France",
    "Germany",
    "Greece",
    "Hungary",
    "Iceland",
    "Ireland",
    "Italy",
    "Kosovo",
    "Latvia",
    "Liechtenstein",
    "Lithuania",
    "Luxembourg",
    "Malta",
    "Moldova",
    "Monaco",
    "Montenegro",
    "Netherlands",
    "North Macedonia",
    "Norway",
    "Poland",
    "Portugal",
    "Romania",
    "Russia",
    "San Marino",
    "Serbia",
    "Slovakia",
    "Slovenia",
    "Spain",
    "Sweden",
    "Switzerland",
    "Ukraine",
    "United Kingdom",
    "Vatican City",
  ],
  // Spanish-speaking countries and regions
  spanishSpeaking: [
    "Argentina",
    "Bolivia",
    "Chile",
    "Colombia",
    "Costa Rica",
    "Cuba",
    "Dominican Republic",
    "Ecuador",
    "El Salvador",
    "Equatorial Guinea",
    "Guatemala",
    "Honduras",
    "Mexico",
    "Nicaragua",
    "Panama",
    "Paraguay",
    "Peru",
    "Puerto Rico",
    "Uruguay",
    "Venezuela",
  ],
};

// All countries list for the dropdown (ensuring uniqueness)
const ALL_COUNTRIES_RAW = [
  ...new Set([
    "United States",
    "Canada",
    "Australia",
    "New Zealand",
    ...COUNTRY_ROUTING.europe,
    ...COUNTRY_ROUTING.spanishSpeaking,
    "Afghanistan",
    "Algeria",
    "Angola",
    "Antigua and Barbuda",
    "Armenia",
    "Azerbaijan",
    "Bahamas",
    "Bahrain",
    "Bangladesh",
    "Barbados",
    "Belize",
    "Benin",
    "Bhutan",
    "Botswana",
    "Brazil",
    "Brunei",
    "Burkina Faso",
    "Burundi",
    "Cambodia",
    "Cameroon",
    "Cape Verde",
    "Central African Republic",
    "Chad",
    "China",
    "Comoros",
    "Congo",
    "Côte d'Ivoire",
    "Djibouti",
    "Dominica",
    "East Timor",
    "Egypt",
    "Eritrea",
    "Eswatini",
    "Ethiopia",
    "Fiji",
    "Gabon",
    "Gambia",
    "Georgia",
    "Ghana",
    "Grenada",
    "Guinea",
    "Guinea-Bissau",
    "Guyana",
    "Haiti",
    "India",
    "Indonesia",
    "Iran",
    "Iraq",
    "Israel",
    "Jamaica",
    "Japan",
    "Jordan",
    "Kazakhstan",
    "Kenya",
    "Kiribati",
    "Kuwait",
    "Kyrgyzstan",
    "Laos",
    "Lebanon",
    "Lesotho",
    "Liberia",
    "Libya",
    "Madagascar",
    "Malawi",
    "Malaysia",
    "Maldives",
    "Mali",
    "Marshall Islands",
    "Mauritania",
    "Mauritius",
    "Micronesia",
    "Morocco",
    "Mozambique",
    "Myanmar",
    "Namibia",
    "Nauru",
    "Nepal",
    "Niger",
    "Nigeria",
    "North Korea",
    "Oman",
    "Pakistan",
    "Palau",
    "Palestine",
    "Papua New Guinea",
    "Philippines",
    "Qatar",
    "Rwanda",
    "Saint Kitts and Nevis",
    "Saint Lucia",
    "Saint Vincent and the Grenadines",
    "Samoa",
    "Sao Tome and Principe",
    "Saudi Arabia",
    "Senegal",
    "Seychelles",
    "Sierra Leone",
    "Singapore",
    "Solomon Islands",
    "Somalia",
    "South Africa",
    "South Korea",
    "South Sudan",
    "Sri Lanka",
    "Sudan",
    "Suriname",
    "Syria",
    "Taiwan",
    "Tajikistan",
    "Tanzania",
    "Thailand",
    "Togo",
    "Tonga",
    "Trinidad and Tobago",
    "Tunisia",
    "Turkey",
    "Turkmenistan",
    "Tuvalu",
    "Uganda",
    "United Arab Emirates",
    "Uzbekistan",
    "Vanuatu",
    "Vietnam",
    "Yemen",
    "Zambia",
    "Zimbabwe",
  ]),
].sort();

// Convert to react-select format
const ALL_COUNTRIES = ALL_COUNTRIES_RAW.map((country) => ({
  value: country,
  label: country,
}));

export const BookingPage = () => {
  const [listings, setListings] = useState("");
  const [country, setCountry] = useState(null);
  const [searchParams] = useSearchParams();
  const showThanks = searchParams.get("thanks") === "true";

  const handleChange = (e) => {
    setListings(e.target.value);
  };

  const handleCountryChange = (selectedOption) => {
    setCountry(selectedOption ? selectedOption.value : "");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!country) {
      return; // Prevent submission if no country selected
    }

    let hubspotLink = "";
    const listingCount = parseInt(listings);

    // 8 listings or below go to webinar (regardless of country)
    if (listingCount <= 8) {
      hubspotLink = COUNTRY_ROUTING.links.zoom;
    }
    // Check if country is in Europe
    else if (COUNTRY_ROUTING.europe.includes(country)) {
      hubspotLink = COUNTRY_ROUTING.links.sara;
    }
    // Check if country is Spanish-speaking (South America, Central America, Mexico, etc.)
    else if (COUNTRY_ROUTING.spanishSpeaking.includes(country)) {
      hubspotLink = COUNTRY_ROUTING.links.sara;
    }
    // All other countries - use listing count logic
    else {
      if (listingCount < 29) {
        hubspotLink = COUNTRY_ROUTING.links.glenn;
      } else {
        hubspotLink = COUNTRY_ROUTING.links.edward;
      }
    }

    window.location.href = hubspotLink;
  };

  return (
    <div
      style={{
        textAlign: "center",
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      {showThanks && (
        <>
          <h2 style={{ color: "#333", marginBottom: "5px" }}>Thank you for signing up to Besty!</h2>
          <p style={{ fontSize: "16px", color: "#555", marginBottom: "10px" }}>We're excited to announce Besty V2! Sign up for a demo below to see what's new:</p>
        </>
      )}
      <div className={styles.wrapper} style={{ padding: "20px", textAlign: "center", minHeight: "auto", position: "relative" }}>
        <div className={styles.bookingPage}>
          <div className={styles.bookingTitle}>Book a demo</div>
          <p className={styles.description}>Please enter the number of listings you operate and select your country.</p>
          <form className={styles.bookingForm} onSubmit={handleSubmit}>
            <input type="number" id="listings" value={listings} onChange={handleChange} placeholder="Number of listings" min="1" required />
            <div style={{ width: "100%", maxWidth: "450px" }}>
              <Select
                id="country"
                value={country ? ALL_COUNTRIES.find((c) => c.value === country) : null}
                onChange={handleCountryChange}
                options={ALL_COUNTRIES}
                placeholder="Select your country"
                isSearchable
                isClearable
                required
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    padding: "8px 4px",
                    fontSize: "16px",
                    border: state.isFocused ? "2px solid #0099fe" : "2px solid #ddd",
                    borderRadius: "12px",
                    boxShadow: state.isFocused ? "0 4px 10px rgba(0, 153, 254, 0.2)" : "0 2px 4px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    textAlign: "left",
                  }),
                  placeholder: (baseStyles) => ({
                    ...baseStyles,
                    color: "#bbb",
                    textAlign: "left",
                  }),
                  singleValue: (baseStyles) => ({
                    ...baseStyles,
                    textAlign: "left",
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                    fontSize: "16px",
                    textAlign: "left",
                  }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "12px",
                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
                  }),
                  option: (baseStyles, state) => ({
                    ...baseStyles,
                    fontSize: "16px",
                    backgroundColor: state.isSelected ? "#0099fe" : state.isFocused ? "#e6f5ff" : "white",
                    color: state.isSelected ? "white" : "#333",
                    cursor: "pointer",
                    textAlign: "left",
                    "&:active": {
                      backgroundColor: "#0099fe",
                    },
                  }),
                }}
              />
            </div>
            <button className={styles.submitBtn} type="submit">
              Submit
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
